import { Component, inject, OnInit } from '@angular/core';
import { TaskType } from '@core/enums';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { SubTaskEntity } from '@core/entities';
import { DateTimeUtility, isMobileView } from '@core/utils';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { DialogRef } from '@ngneat/dialog';

import { ButtonComponent, IconComponent } from '@shared/components/ui';
import { DatePickerInputComponent, FormErrorComponent } from '@shared/components';

@Component({
  selector: 'app-subtask-form',
  templateUrl: './subtask-form.component.html',
  styleUrls: ['./subtask-form.component.scss'],
  imports: [ReactiveFormsModule, ButtonComponent, NgSelectModule, FormErrorComponent, TranslateModule, IconComponent, DatePickerInputComponent],
})
export class SubtaskFormComponent implements OnInit {
  teamMembers: any[];
  dialogRef: any;
  groupParticipants: any[];
  collaboratorsIds: number[];
  taskType: TaskType;
  subtask: SubTaskEntity;
  subTaskData: any;
  form: FormGroup;
  bottomSheetService: any;
  protected readonly TasksType = TaskType;
  protected readonly isMobileView = isMobileView;
  private readonly _formBuilder = inject(FormBuilder);

  constructor() {
    this._initForm();

    if (!isMobileView()) {
      this.dialogRef = inject(DialogRef);
      this.subTaskData = this.dialogRef.data;
      this.taskType = this.subTaskData.taskType as TaskType;
      this.teamMembers = this.subTaskData.teamMembers;
      this.groupParticipants = this.subTaskData.groupParticipants;
      this.collaboratorsIds = this.subTaskData.collaborators;
    } else {
      this.bottomSheetService = inject(NgxBottomSheetService);
      const data = this.bottomSheetService.currentSheetData;
      this.taskType = data.taskType as TaskType;
      this.teamMembers = data.teamMembers;
      this.groupParticipants = data.groupParticipants;
      this.collaboratorsIds = data.collaborators;
    }

    if (this.groupParticipants) {
      this.setGroupParticipantsNames();
    }
  }

  get subtasks(): FormArray {
    return this.form.get('subtasks') as FormArray;
  }

  close(): void {
    if (!isMobileView()) {
      this.dialogRef.close();
    } else {
      this.bottomSheetService.close();
    }
  }

  setGroupParticipantsNames() {
    // set group participants first name and last name from the teamUsers array to the groupParticipants array
    this.groupParticipants.forEach((participant) => {
      const user = this.teamMembers.find((user) => user.id === participant.userId);
      if (user) {
        participant.firstName = user.firstName;
        participant.lastName = user.lastName;
        participant.fullName = `${user.firstName} ${user.lastName}`;
      }
    });
    const userIds = this.groupParticipants.map((participant) => participant.userId);
    this.form.patchValue(
      {
        assignedTo: userIds,
      },
      {
        emitEvent: false,
      },
    );
  }

  ngOnInit(): void {
    if (!isMobileView()) {
      this.subtask = this.dialogRef.data.subTask as SubTaskEntity;
    } else {
      this.subtask = this.bottomSheetService.currentSheetData.subTask as SubTaskEntity;
    }

    if (this.subtask) {
      this.form.patchValue({
        id: this.subtask.id,
        title: this.subtask.title,
        description: this.subtask.description,
        date: DateTimeUtility.getDateOnly(this.subtask.date),
        assignedTo: this.subtask.assignedTo,
        status: this.subtask.status ? this.subtask.status : '0',
      });
    } else {
      if (this.collaboratorsIds && this.collaboratorsIds.length) {
        this.form.patchValue(
          {
            assignedTo: this.collaboratorsIds,
          },
          { emitEvent: false },
        );
      }
    }
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }

    const form = this.form.value;
    form.status = form.status ? form.status : '0';
    form.assigned_to = form.assignedTo.map((id: number) => {
      return { user_id: id, approved_status: 1 };
    });
    delete form.assignedTo;
    if (!isMobileView()) {
      this.dialogRef.close(form);
    } else {
      this.bottomSheetService.close(form);
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  private _initForm(): void {
    this.form = this._formBuilder.group({
      id: [null],
      title: ['', Validators.required],
      description: ['', Validators.required],
      date: [null, Validators.required],
      assignedTo: [null, Validators.required],
      status: '0',
    });
  }
}
